import {locationUploadScope} from '../upload-history/upload-history.types';
import {UploadHistoryComponent} from '../upload-history/upload-history.component';
import {AfterViewInit, Component} from '@angular/core';
import {connectionConsumptionType} from './connection-consumption-type';
import {selectUploadConnections} from './connection-upload-selection';
import {rawConnectionUploadTimeRange} from './connection-upload-time-range';
import {View} from "src/app/common/view";
import {Handler} from "src/app/common/handler";
import {AppContext} from "../../../../app-context";
import {
    AggregatedDataPoint,
    Connection,
    ConnectionType,
    DataType,
    ExportConnectionData,
    Organisation,
    TimeResolution,
    UploadMeterData
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {map, Observable, of} from "rxjs";
import * as XLSX from 'xlsx-js-style';
import {Entity} from "../../../../handlers/entity";
import {filterByTermArray, localTimeFormat, removeItem, sort} from "../../../../common/utils";
import {RefdataUtils} from "../../refdata-utils";
import {ConnectionComponentData, ConnectionDetailsComponent} from "../connection-details/connection-details.component";
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {DateFieldRange} from "../../../../common/date/date-range/date-field-range";
import {SourcesProvider} from "../../../../utils/source-providers/sources-provider";
import {downloadConnectionsAsExcel, uploadConnectionsExcel} from "./connections-list-excel";
import {
    ArrayTemplate,
    Cell,
    datedExcelFileName,
    DateTimeField,
    downloadWorkbook,
    exportDataAsExcel,
    exportExcelFromWorkBook,
    Field,
    getWorkbook,
    parseExcel,
    Parser,
    QuantityField,
    rangedExcelFileName,
    RequiredField,
    ValidatedField,
    WorkBookTemplate
} from "../../../../common/upload/excel.utils";
import {AppCommonUtils, sendQuery} from "../../../../common/app-common-utils";
import moment from "moment";
import {openConfirmationModalWithCallback} from "../../../../app-utils";
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from "../../../../common/modal-confirm/modal-confirm.component";
import {isMeterVisible} from '../../meters/submeter-role.utils';
import lodash, {cloneDeep} from "lodash";
import {downloadBulkMonthData} from "./bulk-data-download";
import {ComparatorChain} from "../../../../common/comparator-chain";
import {HandleCommand} from "../../../../common/handle-command";
import {OrganisationProvider} from "../../../../utils/source-providers/organisation-provider";
import {
    buildConnectionDataExportTemplate,
    buildThermalDataExport,
    thermalDataExportTemplate,
    connectionExportSheetNames,
    connectionCsvDataToJson,
    connectionDataExportResolution,
    connectionsForResolution,
    mergeChargePointDataIntoElectricity
} from './connection-data-export';
import {TranslateDirective} from "../../../../common/utils/translate.directive";

@Component({
    selector: 'app-connections-overview',
    templateUrl: './connections-overview.component.html',
    styleUrls: ['./connections-overview.component.scss']
})
@Handler()
export class ConnectionsOverviewComponent extends View implements AfterViewInit {
    appContext = AppContext;
    dashboardContext = DashboardContext;
    refDataUtils = RefdataUtils;
    organisation: Organisation;

    term: string;
    query: Observable<Entity[]>;
    data: Entity[] = [];
    selectedRecords: Entity[] = [];

    bulkDownloading: boolean;
    selecting: boolean;
    maxSelectableItems: number = 10;

    downloadConnectionsAsExcel = () => this.queryFilters(this.sendQuery("getConnectionsAsEntities")).subscribe(c => downloadConnectionsAsExcel(c));
    connectionCsvDataToJson = connectionCsvDataToJson;
    connectionDataTemplate = connectionDataExportTemplate;

    defaultDownloadRange = DashboardContext.defaultRange;
    bulkDownloadTimeRange: DateFieldRange = this.defaultDownloadRange();
    sourceProvider: SourcesProvider<any>;

    ngAfterViewInit() {
        this.createQuery();
        this.subscribeTo("getOrganisation").subscribe(o => {
            this.organisation = o;
            this.sourceProvider = new OrganisationProvider(this.organisation?.organisationId);
        });
    }

    createQuery = () => {
        this.query = this.queryFilters(this.subscribeTo("getConnectionsAsEntities"));
    }

    executeQuery = () => this.query.subscribe().unsubscribe();

    queryFilters = (o: Observable<Entity[]>): Observable<Entity[]> => {
        return o.pipe(map((entities: Entity[]) => entities.filter(filterByTermArray([this.term], Entity.getExcludedFilterFields()))))
            .pipe(map(e => sort(e, RefdataUtils.connectionsComparator)));
    }

    trackByForRecord = (index: number, record: Entity) => record.connection.connectionId;

    createNewConnection = () => this.openModal(ConnectionDetailsComponent, <ConnectionComponentData>{
        organisation: this.organisation,
        locationId: null,
        connection: null
    });

    @HandleCommand("selectionChange")
    selectionChange(record: { entity: Entity, selected: boolean }) {
        if (record.selected) {
            if (this.maxSelectableItems && this.selectedRecords.length === this.maxSelectableItems) {
                AppCommonUtils.registerError("A maximum of 10 records is allowed to be selected");
                setTimeout(() => delete record.entity['selected'], 0);
                return;
            }
            if (!this.selectedRecords.includes(record.entity)) {
                this.selectedRecords.push(record.entity);
            }
        } else {
            this.selectedRecords = removeItem(this.selectedRecords, record.entity);
        }
        record['selected'] = record.selected;
    }

    toggleSelection = () => {
        this.selecting = !this.selecting;
        if (!this.selecting) {
            this.selectedRecords.forEach(c => delete c['selected']);
            this.selectedRecords = [];
        }
    }

    bulkDownloadMonthData(event: any) {
        downloadBulkMonthData(this.sourceProvider, this.bulkDownloadTimeRange);
    }

    downloadMeterAuthorisationPeriods = () => {
        let header = ["Organisation", "Location", "Building code", "EAN", "Market segment",
            "Connection Type", "Meter id", "Meter type", "Start", "End"];
        const data: any[] = [header];
        sendQuery("getOrganisations").subscribe(organisations => organisations.forEach(o => o.locations.forEach(l => l.connections.forEach((c : Connection) => c.meters.filter(m => !m.info?.virtual && isMeterVisible(m)).forEach(m => {
            let start = m.timeRange?.start;
            let end = c.info.desiredEndDate
            if (end && moment(end).diff(moment(), 'years') > 10) {
                end = null;
            }
            if (end && moment(end).isBefore(moment(start))) {
                start = m.info.authorizedFrom;
            }
            const entry: any[] = [
                RefdataUtils.organisationInfoFormatter(o.info),
                RefdataUtils.locationInfoFormatter(l.info),
                l.info.buildingCode,
                c.info.code,
                c.info.marketSegment,
                c.info.connectionType,
                m.meterId,
                m.info.type,
                start && moment(start).toDate(),
                end && moment(end).toDate()];
            data.push(entry);
        })))));
        exportDataAsExcel(data, datedExcelFileName(
            TranslateDirective.getTranslation('meter authorisation periods', true) as string));
    }

    downloadMeterList = () => {
        // This export is used for billing purposes,
        // so manual uploads are excluded and multiple primary meters with the same EAN are grouped together

        let header = ["Organisation", "Location", "Building code", "EAN", "Market segment",
            "Connection Type", "Meter id(s)", "Meter type", "Start", "End"];
        const data: any[] = [header];
        sendQuery("getOrganisations").subscribe(organisations => {
            organisations.forEach(o => o.locations.forEach(l => l.connections.forEach((c: Connection) => {
                const filteredMeters = c.meters.filter(m => !m.info?.virtual && !m.meterId.includes("upload-") && isMeterVisible(m));
                const primaryMeters = filteredMeters.filter(m => m.info.type === "PRIMARY");
                const otherMeters = filteredMeters.filter(m => m.info.type !== "PRIMARY");

                if (primaryMeters.length > 1) {
                    const meterIds = primaryMeters.map(m => m.meterId).join(", ");

                    const starts = primaryMeters.map(m => m.timeRange?.start).filter(Boolean);
                    let earliestStart = starts.length ? moment.min(starts.map(s => moment(s))) : null;

                    const ends = primaryMeters.map(m => {
                        let end = c.info.desiredEndDate;
                        if (end && moment(end).diff(moment(), 'years') > 10) {
                            end = null;
                        }
                        if (end && earliestStart && moment(end).isBefore(moment(earliestStart))) {
                            earliestStart = moment(m.info.authorizedFrom);
                        }
                        return end;
                    }).filter(Boolean);

                    const latestEnd = ends.length ? moment.max(ends.map(e => moment(e))).subtract(1, 'day') : null;

                    const entry: any[] = [
                        RefdataUtils.organisationInfoFormatter(o.info),
                        RefdataUtils.locationInfoFormatter(l.info),
                        l.info.buildingCode,
                        c.info.code,
                        c.info.marketSegment,
                        c.info.connectionType,
                        meterIds,
                        primaryMeters[0].info.type,
                        earliestStart ? earliestStart.toDate() : null,
                        latestEnd ? latestEnd.toDate() : null
                    ];
                    data.push(entry);
                }

                if (primaryMeters.length === 1) {
                    otherMeters.push(primaryMeters[0]);
                }

                otherMeters.forEach(m => {
                    let start = m.timeRange?.start;
                    let end = c.info.desiredEndDate;
                    if (end && moment(end).diff(moment(), 'years') > 10) end = null;
                    if (end && start && moment(end).isBefore(moment(start))) {
                        start = m.info.authorizedFrom;
                    }
                    const entry: any[] = [
                        RefdataUtils.organisationInfoFormatter(o.info),
                        RefdataUtils.locationInfoFormatter(l.info),
                        l.info.buildingCode,
                        c.info.code,
                        c.info.marketSegment,
                        c.info.connectionType,
                        m.meterId,
                        m.info.type,
                        start ? moment(start).toDate() : null,
                        end ? moment(end).subtract(1, 'day').toDate()  : null
                    ];
                    data.push(entry);
                });
            })));
        });
        exportDataAsExcel(data, datedExcelFileName(
            TranslateDirective.getTranslation('meter list', true) as string));
    }

    onUploadConnectionsList = (event: any) => {
        uploadConnectionsExcel(event.files[0]).subscribe(command => {
            if (command.updates.length === 0) {
                AppCommonUtils.registerError("No changes have been found between uploaded file and current data", 'warning', 3000);
                return;
            }
            const eanCodes = command.updates.map(c => c.info.code).map(ean => `<li>${ean}</li>`).join("");
            openConfirmationModalWithCallback((confirmed) => {
                if (confirmed) {
                    this.sendCommand("com.flowmaps.api.organisation.UpsertConnections", command,
                        () => AppCommonUtils.registerSuccess('Connections were uploaded successfully'));
                }
            }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
                type: "warning",
                title: "Bulk update connections",
                innerHtmlMessage: `<p><span>You are about to bulk update the following connections</span></p><ul class="notranslate fw-bold">${eanCodes}</ul><p class="fw-bold">Are you sure you want to execute this action?</p>`,
                confirmText: "Update connections",
                cancelText: "Cancel"
            }, 'static');
        });
        event.value = '';
    }

    openUploadHistory = () => this.queryFilters(this.sendQuery("getConnectionsAsEntities")).subscribe(connections => {
        this.openModal(UploadHistoryComponent, {
            organisationId: this.organisation?.organisationId,
            ...locationUploadScope(connections)
        }, {closeOnSuccess: false, scrollable: true, cssClass: 'modal-xl upload-history-modal'});
    });

    private submitMeterUpload(command: UploadMeterData, file: File) {
        this.sendQuery('getConnectionsAsEntities').subscribe((entities: Entity[]) => {
            const ids = Object.keys(command.connectionData);
            const uploaded = entities.filter(entity => ids.includes(entity.connection.connectionId));
            this.openModal(UploadHistoryComponent, {
                ...(uploaded.length === ids.length ? locationUploadScope(uploaded) : {connectionIds: ids}),
                pendingUpload: {file}
            }, {closeOnSuccess: false, scrollable: true, cssClass: 'modal-xl upload-history-modal'});
        });
    }

    onUploadConnectionData = (event: any) => {
        const file: File = event.files?.[0];
        event.value = '';
        if (!file) return;
        getWorkbook(file).subscribe(workbook => {
            const hasThermalColumns = workbook.SheetNames.some(name =>
                /^(warmte|heat)\s*\(GJ\)$/i.test(workbook.Sheets[name]['C1']?.v || ''));
            if (hasThermalColumns) this.uploadRawFile(file, workbook);
            else this.uploadMonthlyFile(file);
        });
    };

    private uploadMonthlyFile(file: File): void {
        parseExcel(file, connectionDataImportTemplate)
            .subscribe(e => {
                sendQuery("getConnectionsAsEntities").subscribe(connections => {
                    connections = this.uploadConnections(connections.filter(c => e.dataPerEan.some(r => r.ean === c.connection.info.code)));
                    if (!connections.length) return;
                    const connectionWithData = connections.map(c => {
                        const dataPerEan = e.dataPerEan.find(r => r.ean === c.connection.info.code);
                        return dataPerEan ? {
                            connection: c,
                            dataPerEan: dataPerEan
                        } : null;
                    }).filter(c => c);
                    const command: UploadMeterData = {
                        connectionData: {}
                    };
                    connectionWithData.forEach(c => {
                        command.connectionData[c.connection.connection.connectionId] = getDataPointsOfConsumption(
                            e.year, this.getDataType(c.connection.connection), c.dataPerEan.consumptions);
                    });
                    this.submitMeterUpload(command, file);
                });
            });

        function getDataPointsOfConsumption(year: number, dataType: DataType, consumptions: { [key: string]: number }):
            { [P in DataType]?: AggregatedDataPoint[] } {
            const returnValue: { [P in DataType]?: AggregatedDataPoint[] } = {};
            Object.entries(consumptions).map((e, monthIndex) => {
                const startDate = moment({year, month: monthIndex, day: 1});
                return {
                    type: dataType,
                    value: e[1],
                    timeRange: {
                        start: `${startDate.format("YYYY-MM-DDTHH:mm:ss")}`,
                        end: `${startDate.clone().add(1, "month").format("YYYY-MM-DDTHH:mm:ss")}`
                    }
                }
            }).filter(d => d.value !== null && d.value !== undefined)
                .forEach(
                r => {
                    if (!returnValue[r.type]) {
                        returnValue[r.type] = [];
                    }
                    returnValue[r.type].push({
                        value: r.value,
                        timeRange: r.timeRange
                    });
                });
            return returnValue;
        }
    }

    uploadMinuteData = (event: any) => {
        const file: File = event.files?.[0];
        event.value = '';
        if (!file) return;
        getWorkbook(file).subscribe(workbook => this.uploadRawFile(file, workbook));
    };

    private uploadRawFile(file: File, workbook: XLSX.WorkBook): void {
        parseExcel(file, {
            sheets: workbook.SheetNames.map(s => ({
                name: s,
                template: {
                    [s]: new ArrayTemplate(rawConnectionDataImportTemplate(workbook.Sheets[s]),
                        [2, parseInt(workbook.Sheets[s]["!ref"].match(/(\d+)$/)[0])])
                }
            }))
        })
            .subscribe(data => {
                sendQuery("getConnectionsAsEntities").subscribe(connections => {
                    connections = this.uploadConnections(connections.filter(c => data[c.connection.info.code]));
                    if (!connections.length) return;
                    const connectionWithData = connections
                        .filter(c => data[c.connection.info.code])
                        .map(c => ({
                            connection: c,
                            dataPerEan: data[c.connection.info.code]
                        }));
                    const command: UploadMeterData = {
                        connectionData: {}
                    };
                    connectionWithData.forEach(c => addConnectionData(c, command));
                    this.submitMeterUpload(command, file);
                });
            });

        let addConnectionData = (c: { dataPerEan: any; connection: Entity }, command: UploadMeterData) => {
            const consumptionDataType = this.getDataType(c.connection.connection);
            const productionDataType = this.getProductionDataType(c.connection.connection);
            const result: { [key: string]: AggregatedDataPoint[] } = {
                [consumptionDataType]: []
            };
            if (productionDataType) {
                result[productionDataType] = [];
            }
            c.dataPerEan.forEach(r => {
                const interval = {
                    timeRange: rawConnectionUploadTimeRange(r.start, r.end),
                    ...(r.utcOffsetSeconds != null ? {utcOffsetSeconds: r.utcOffsetSeconds} : {}),
                    ...(r.durationSeconds != null ? {durationSeconds: r.durationSeconds} : {})
                };
                const add = (dataType: DataType, value: number) => {
                    if (value == null) return;
                    (result[dataType] ||= []).push({value, ...interval});
                };
                if (c.connection.connection.info.connectionType === ConnectionType.Heat) {
                    add(DataType.heatConsumption, r.heatConsumption);
                    add(DataType.coolingConsumption, r.coolingConsumption);
                }
                if (r.consumption !== null && r.consumption !== undefined) {
                    result[consumptionDataType].push({
                        value: r.consumption,
                        ...interval
                    });
                }
                if (productionDataType && r.feedIn !== null && r.feedIn !== undefined) {
                    result[productionDataType].push({
                        value: r.feedIn,
                        ...interval
                    });
                }
            });
            command.connectionData[c.connection.connection.connectionId] = Object.fromEntries(
                Object.entries(result).filter(([, points]) => points.length));
        }
    }

    private uploadConnections(connections: Entity[]): Entity[] {
        try {
            return selectUploadConnections(connections,
                this.selectedRecords.map(entity => entity.connection.connectionId));
        } catch (error) {
            AppCommonUtils.registerError(error.message);
            return [];
        }
    }

    private getDataType = (connection: Connection): DataType =>
        connectionConsumptionType(connection.info.connectionType);

    private getProductionDataType = (connection: Connection): DataType => {
        switch (connection.info.connectionType) {
            case "Electricity":
                return DataType.electricityFeedIn;
            default:
                return null;
        }
    }
}

export function downloadRawConnectionData(timeRange: DateFieldRange, resolution: TimeResolution,
                                          selectedItems: Entity[], optionalDataTypes: DataType[] = [], csvMapper: (csv: string) => any[], comparator: ComparatorChain, onDownloaded: () => void = null) {
    const downloadableItems = connectionsForResolution(selectedItems, resolution);
    if (downloadableItems.length === 0) {
        return;
    }
    const requestedTypes = [...new Set([...optionalDataTypes, DataType.coolingConsumption])];
    sendQuery("com.flowmaps.api.measurements.ExportConnectionData", <ExportConnectionData>{
        timeRange: {
            start: moment(timeRange.start).format(localTimeFormat),
            end: moment(timeRange.end).format(localTimeFormat)
        },
        resolution: resolution,
        ids: downloadableItems.map(c => c.connection.connectionId),
        optionalDataTypes: requestedTypes,
    }, {
        showSpinner: true,
        responseType: "text"
    }).subscribe(csv => {
        const preparedExport = mergeChargePointDataIntoElectricity(downloadableItems, connectionCsvDataToJson(csv, requestedTypes));
        downloadData(preparedExport.connections.sort(comparator.compare), preparedExport.rows, resolution, timeRange);
        if (onDownloaded) {
            onDownloaded();
        }
    })
}

function downloadData(connections: Entity[], data: any[], resolution: TimeResolution, timeRange: DateFieldRange) {
    const thermalSheets = buildThermalDataExport(connections, data);
    const thermalById = new Map(thermalSheets.map(sheet => [sheet.source.connection.connectionId, sheet]));
    const isThermal = (connection: Entity) => connection.connection.info.connectionType === ConnectionType.Heat
        || connection.connection.info.connectionType === ConnectionType.Cooling;
    const exportedConnections = lodash.uniqBy(connections, c => c.connection.connectionId)
        .filter(c => !isThermal(c) || thermalById.has(c.connection.connectionId));
    const dataGrouped = lodash.groupBy(data, row => row.connectionId);
    thermalSheets.forEach(sheet => dataGrouped[sheet.source.connection.connectionId] = sheet.rows);
    const sheetNames = connectionExportSheetNames(exportedConnections);
    const sheetTemplate = (connection: Entity) => isThermal(connection) ? thermalDataExportTemplate(dataGrouped[connection.connection.connectionId] || [])
        : buildConnectionDataExportTemplate(dataGrouped[connection.connection.connectionId] || [], connection.connection.info.connectionType);
    const template: WorkBookTemplate = {
        sheets: exportedConnections.map(connection => ({
            name: sheetNames.get(connection.connection.connectionId),
            template: {
                [connection.connection.connectionId]: new ArrayTemplate(sheetTemplate(connection),
                    [2, (dataGrouped[connection.connection.connectionId]?.length || 0) + 1])
            }
        }))
    };
    exportExcelFromWorkBook(
        (exportedConnections.every(isThermal) ? of(XLSX.utils.book_new())
            : downloadWorkbook("/assets/templates/connections-data-download.xlsx")).pipe(
            map(sourceWorkbook => {
                const workbook = XLSX.utils.book_new();
                exportedConnections.forEach(connection => {
                    const sheet = cloneDeep(sourceWorkbook.Sheets["Connections"] || {});
                    Object.keys(sheet).filter(key => /^[A-Z]+1$/.test(key)).forEach(key => delete sheet[key]);
                    let totalCols: number;
                    if (isThermal(connection)) {
                        ['Start', 'End', 'Heat (GJ)', 'Cooling (GJ)'].forEach((label, column) => {
                            sheet[XLSX.utils.encode_cell({r: 0, c: column})] = {
                                t: 's', v: TranslateDirective.getTranslation(label, true) as string
                            };
                        });
                        totalCols = 4;
                    } else {
                        const fields = sheetTemplate(connection);
                        totalCols = applyDynamicColumnHeaders(sheet, fields,
                            applyBaseColumnHeaders(sheet, connection.connection.info.connectionType, fields));
                    }
                    sheet["!ref"] = XLSX.utils.encode_range({s: {r: 0, c: 0}, e: {r: 1, c: totalCols - 1}});
                    XLSX.utils.book_append_sheet(workbook, sheet, sheetNames.get(connection.connection.connectionId));
                });
                return workbook;
            })
        ),
        template,
        dataGrouped,
        rangedExcelFileName(TranslateDirective.getTranslation('raw connection data', true) as string, timeRange),
        {
            sheets: Object.fromEntries(exportedConnections.filter(connection => !isThermal(connection))
                .map(connection => [sheetNames.get(connection.connection.connectionId), {
                    title: rawDataExportTitle(connection.connection.info.connectionType,
                        connectionDataExportResolution(dataGrouped[connection.connection.connectionId] || [], resolution)),
                    headerComments: {
                        [AppContext.measurementName(DataType.chargePointConsumption, true)]:
                            TranslateDirective.getTranslation('charging consumption excel comment', true) as string
                    }
                }]))
        }
    );
}

const BASE_COLUMNS: Array<{
    key: 'start' | 'end' | 'consumption' | 'feedIn' | 'production';
    label: string;
}> = [
    { key: 'start', label: 'Start' },
    { key: 'end', label: 'End' },
    { key: 'consumption', label: 'Consumption' },
    { key: 'feedIn', label: 'Feed in' },
    { key: 'production', label: 'Production' }
];

function applyBaseColumnHeaders(ws: XLSX.WorkSheet, connectionType: string, sheetTemplate: any): number {
    const visibleColumns = BASE_COLUMNS.filter(column => Object.prototype.hasOwnProperty.call(sheetTemplate, column.key));
    visibleColumns.forEach((col, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
        let label = TranslateDirective.getTranslation(col.key === 'feedIn' ? 'show feed-in'
            : col.key === 'production' ? 'show production' : col.label, true) as string;
        switch (col.key) {
            case 'consumption':
                label += ` (${DashboardContext.getConsumptionUnit(connectionType)})`;
                break;
            case 'feedIn':
                if (connectionType === 'Electricity') {
                    label += ` (${DashboardContext.getMeasurementUnit(DataType.electricityFeedIn)})`;
                }
                break;
            case 'production':
                if (connectionType === 'Electricity') {
                    label += ` (${DashboardContext.getMeasurementUnit(DataType.electricityGrossProduction)})`;
                }
                break;
        }
        ws[cellRef] = { t: 's', v: label };
    });
    return visibleColumns.length;
}

export function rawDataExportTitle(connectionType: string, resolution: TimeResolution): string {
    const measurementKey = connectionType === ConnectionType.Electricity ? 'electricity consumption'
        : connectionType === ConnectionType.Gas ? 'gas consumption'
            : connectionType === ConnectionType.Heat ? 'heat consumption'
                : connectionType === ConnectionType.Cooling ? 'cooling consumption'
                    : connectionType === ConnectionType.Water ? 'water consumption'
                    : 'charge point consumption';
    return `${TranslateDirective.getTranslation(measurementKey, true)} Ã¢â‚¬â€œ ${rawDataResolutionLabel(resolution)}`;
}

function rawDataResolutionLabel(resolution: TimeResolution): string {
    const resolutionKey = resolution == null ? 'available intervals'
        : resolution === TimeResolution.minute ? 'quarter-hour data'
        : resolution === TimeResolution.hour ? 'hourly data'
            : resolution === TimeResolution.day ? 'daily data'
                : resolution === TimeResolution.month ? 'monthly data' : 'yearly data';
    return TranslateDirective.getTranslation(resolutionKey, true) as string;
}

function applyDynamicColumnHeaders(ws: XLSX.WorkSheet, sheetTemplate: any, colOffset: number): number {
    const baseKeys = BASE_COLUMNS.map(c => c.key);
    const dynamicColumns = Object.keys(sheetTemplate)
        .filter(k => !baseKeys.includes(k as any));
    dynamicColumns.forEach((key, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colOffset + i });
        if (key === 'utcOffsetSeconds' || key === 'durationSeconds') {
            ws[cellRef] = {t: 's', v: TranslateDirective.getTranslation(key, true) as string};
            return;
        }
        const type = key as DataType;
        const label = AppContext.measurementName(type, true);
        const unit = type === DataType.sunHours
            ? '%'
            : DashboardContext.getMeasurementUnit(type);

        ws[cellRef] = { t: 's', v: `${label} (${unit})` };
    });
    return colOffset + dynamicColumns.length;
}

const connectionDataImportTemplate = {
    sheets: [
        {
            name: 'Data',
            template: {
                year: new RequiredField('B1'),
                dataPerEan: new ArrayTemplate({
                    ean: new RequiredField('A$'),
                    consumptions: {
                        january: new QuantityField('B$'),
                        february: new QuantityField('C$'),
                        march: new QuantityField('D$'),
                        april: new QuantityField('E$'),
                        may: new QuantityField('F$'),
                        june: new QuantityField('G$'),
                        july: new QuantityField('H$'),
                        august: new QuantityField('I$'),
                        september: new QuantityField('J$'),
                        october: new QuantityField('K$'),
                        november: new QuantityField('L$'),
                        december: new QuantityField('M$'),
                    }
                }, [3, 100])
            }
        }
    ]
}

const connectionDataExportTemplate = {
    start: new ValidatedField(new DateTimeField('A$'), (value: any, cell: Cell, parser: Parser, cellNameFunction) => {
        const start = moment(value, "DD-MM-YYYY HH:mm:ss", true);
        const end = moment(Field.getCellValue(parser.mapAny(connectionDataExportTemplate.end, cellNameFunction)), "DD-MM-YYYY HH:mm:ss", true);
        if (!start.isValid() || !end.isValid()) {
            throw "Start or end date is not a valid format";
        }
        if (start.isSameOrAfter(end)) {
            throw 'Start date cannot be the same or later than end date.';
        }
    }),
    end: new DateTimeField('B$'),
    consumption: new QuantityField('C$'),
    feedIn: new QuantityField('D$'),
    production: new QuantityField('E$'),
}

const thermalConnectionDataImportTemplate = {
    start: connectionDataExportTemplate.start,
    end: connectionDataExportTemplate.end,
    heatConsumption: new QuantityField('C$'),
    coolingConsumption: new QuantityField('D$')
};

// Keep the offset/duration columns from Flowmaps exports during re-import, in either supported language.
function rawConnectionDataImportTemplate(sheet: XLSX.WorkSheet): any {
    const thermal = /^(warmte|heat)\s*\(GJ\)$/i.test(sheet['C1']?.v || '');
    const template: any = {...(thermal ? thermalConnectionDataImportTemplate : connectionDataExportTemplate)};
    const headers: Record<string, string> = {};
    const width = XLSX.utils.decode_range(sheet['!ref']).e.c;
    for (let column=0; column<=width; column++) {
        const letter = XLSX.utils.encode_col(column);
        headers[String(sheet[letter+'1']?.v || '').toLowerCase().replace(/[^a-z0-9]/g, '')] = letter;
    }
    const find = (names: string[]) => names.map(name => headers[name]).find(Boolean);
    const offset = find(['utcoffsetseconds', 'utcoffsetatstartseconds', 'utctijdverschilbijstartseconden']);
    const duration = find(['durationseconds', 'intervaldurationseconds', 'intervalduurseconden']);
    if (offset) {
        // Local end labels can equal or precede start labels during the winter-time fold.
        // The server validates ordered physical instants and their location time zone.
        template.start = new DateTimeField('A$');
        template.utcOffsetSeconds = new QuantityField(offset+'$');
    }
    if (duration) template.durationSeconds = new QuantityField(duration+'$');
    return template;
}
